'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, Bell, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { AccountDrawer, getAccountInitials } from '@/components/layout/account-drawer';
import { useSessionSummary } from '@/hooks/useSessionSummary';

export function MobileHomeHeader() {
  const { summary } = useSessionSummary();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const initials = getAccountInitials(summary?.fullName, summary?.email ?? '');

  return (
    <header
      dir="rtl"
      className="sticky top-0 z-30 flex items-center justify-between shadow-md bg-background px-4 py-2"
    >
      <Link href="/" aria-label="ValleyTech الرئيسية" className="flex items-center">
        <Image src="/valleytech.svg" alt="ValleyTech" width={150} height={36} className="h-12 w-auto" priority />
      </Link>
      <div className="flex items-center gap-1  ">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="w-8 h-8 "
          aria-label="التنبيهات"
          onClick={() => setIsNotificationsOpen(true)}
        >
          <Bell className="h-8 w-8" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="الحساب"
          onClick={() => setIsDrawerOpen(true)}
        >
          {summary ? (
            <span
              aria-hidden
              className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground"
            >
              {initials}
            </span>
          ) : (
            <span
              aria-hidden
              className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card text-muted-foreground"
            >
              <UserRound className="h-8 w-8" />
            </span>
          )}
        </Button>
      </div>
      <AccountDrawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen} />
      <NotificationsSheet open={isNotificationsOpen} onOpenChange={setIsNotificationsOpen} />
    </header>
  );
}

function NotificationsSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl border-t border-border">
        <SheetHeader className="items-center text-center">
          <span aria-hidden className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Bell className="h-6 w-6 text-muted-foreground" />
          </span>
          <SheetTitle className="text-base font-bold">لا توجد تنبيهات جديدة</SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            أنت على اطلاع بكل جديد في ValleyTech
          </SheetDescription>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
}

interface MobilePageHeaderProps {
  title: string;
  backHref?: string;
  action?: React.ReactNode;
  children?: React.ReactNode;
}

export function MobilePageHeader({ title, backHref = '/', action, children }: MobilePageHeaderProps) {
  return (
    <header
      dir="rtl"
      className="sticky top-0 z-30 space-y-3 border-b border-border bg-background px-4 py-3"
    >
      <div className="flex items-center justify-between">
        <Link
          href={backHref}
          aria-label="رجوع"
          className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground"
        >
          <ArrowRight className="h-6 w-6" />
        </Link>
        <h1 className="min-w-0 flex-1 truncate text-center text-base font-bold">{title}</h1>
        <div className="flex h-10 min-w-10 items-center justify-center text-muted-foreground">{action}</div>
      </div>
      {children}
    </header>
  );
}
